var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import mapAuthenticationContext from '../../methods/mapAuthenticationContext';
import { ApiService } from '../api-service/api-service';
import { AuthenticationMethodType, AuthenticationReason, AuthenticationSubject, ConsumerIdentityType } from '@mc-alberta/types';
import { LooseMap } from '../../utils/loose-map/loose-map';
import { getAuthMethodTypesForStrategyArray } from '../../sdk/utils/getAuthMethodTypes';
import { KPI_RESOURCE, VERIFICATION_METHOD, VERIFICATION_RESULT } from '../../constants';
import { getLookupKpiMeta } from '../../sdk/utils';
import { KpiOperation } from '../../types/enums/KpiOperation';
import { KpiState } from '../../types/enums/KpiState';
/**
 * This is an adapter that will handle doing the lookup to backend services. Here we also perform
 * some memoization to avoid extra network calls.
 *
 * This is also needed in order to include some temporary unstable hacks due to time constraints
 * not allowing backend to complete all their work.
 */
export class LookupService {
    constructor(starkInstanceParams, kpi) {
        this.starkInstanceParams = starkInstanceParams;
        this.kpi = kpi;
        this.authenticationMethodMemo = new LooseMap();
    }
    /**
     * Performs lookup to the appropriate API service to fetch authentication info based on the
     * authentication instance details provided.
     *
     * This method memoizes its results so that subsequent lookups do not perform additional XHR
     * calls, reducing latency at the cost of some extra storage space. This memoization is
     * serialized when Stark is ported from one app context to another and restored to save the memo
     * optimizations.
     */
    lookup({ authenticationInstance, cardToken, idToken }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { authenticationStrategies } = authenticationInstance;
            const promises = Object.keys(authenticationStrategies)
                .map((authenticationReason) => authenticationReason)
                .map((authenticationReason) => {
                const requestedMethodTypes = getAuthMethodTypesForStrategyArray(authenticationStrategies[authenticationReason] || []);
                return this.lookupProxy({
                    authenticationInstance,
                    authenticationReason,
                    cardToken,
                    idToken,
                    requestedMethodTypes
                });
            });
            const results = yield Promise.all(promises);
            return this.mergeLookupResults(results);
        });
    }
    /**
     * Removes the memoized `FIDO2` method for a `CONSUMER` account reference.
     */
    unmemoizeFidoForConsumerAuth() {
        try {
            Array.from(this.authenticationMethodMemo.keys())
                .map((key) => JSON.parse(key))
                .filter(({ accountReference, requestedMethodType }) => {
                return (accountReference.consumerIdentity &&
                    requestedMethodType === AuthenticationMethodType.FIDO2);
            })
                .forEach(this.authenticationMethodMemo.delete);
        }
        catch (error) {
            console.error(error);
        }
    }
    /**
     * Aggregate all of the methods supported from every reason, and have the strategies themselves
     * determine whether or not they can be used.
     */
    mergeLookupResults(results) {
        const mergedResults = results.reduce((mergedResults, result) => {
            const { assuranceData, authenticationMethods } = result;
            // TODO: this is only temporary -- auth provider will fix this next iteration
            const verificationData = assuranceData.verificationData.map((verificationData) => {
                return Object.assign(Object.assign({}, verificationData), { verificationResults: [
                        VERIFICATION_METHOD.VEHICLE,
                        VERIFICATION_METHOD.IDV_EXTERNAL
                    ].includes(verificationData.verificationMethod)
                        ? VERIFICATION_RESULT.VERIFIED
                        : verificationData.verificationResults });
            });
            // Merge authentication methods
            mergedResults.authenticationMethods =
                mergedResults.authenticationMethods.concat(authenticationMethods);
            // Merge verification data
            mergedResults.assuranceData.verificationData =
                mergedResults.assuranceData.verificationData.concat(verificationData);
            return mergedResults;
        }, {
            authenticationMethods: [],
            assuranceData: { verificationData: [] }
        });
        const dedupedAuthenticationMethods = Array.from(new Set(mergedResults.authenticationMethods));
        return {
            assuranceData: mergedResults.assuranceData,
            authenticationMethods: dedupedAuthenticationMethods
        };
    }
    /**
     * Get look up results from the memo, or make XHR calls to the API if we don't have it already.
     *
     * This is a proxy between the two and the functions invoked with this proxy should be the only
     * places the memo is accessed.
     */
    lookupProxy({ authenticationInstance, authenticationReason, cardToken, idToken, requestedMethodTypes }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { mergedAuthMethods, mergedVerificationData, pendingAuthMethodTypes } = this.askMemoForInfoOnRequestedTypes({
                authenticationInstance,
                authenticationReason,
                requestedMethodTypes
            });
            // Look up anything we don't already know
            if (pendingAuthMethodTypes.size > 0) {
                const results = yield this.lookupForPendingMethodTypes({
                    authenticationInstance,
                    authenticationReason,
                    cardToken,
                    idToken,
                    pendingAuthMethodTypes: Array.from(pendingAuthMethodTypes)
                });
                // Extract data for supported method types
                pendingAuthMethodTypes.forEach((authenticationMethodType) => {
                    const authenticationMethodsForCurrType = results.authenticationMethods.filter((authenticationMethod) => {
                        return authenticationMethod.authenticationMethodType === authenticationMethodType;
                    });
                    const { verificationData } = results.assuranceData || {};
                    verificationData === null || verificationData === void 0 ? void 0 : verificationData.forEach((verificationData) => {
                        mergedVerificationData.add(verificationData);
                    });
                    authenticationMethodsForCurrType.forEach((method) => {
                        mergedAuthMethods.add(method);
                    });
                    /**
                     * This memo entry should never already exist at this point, since we would've skipped
                     * entering this iteration if we found info on it previously.
                     */
                    this.authenticationMethodMemo.set({
                        accountReference: authenticationInstance.accountReference,
                        authenticationReason,
                        requestedMethodType: authenticationMethodType
                    }, {
                        authenticationMethods: new Set(authenticationMethodsForCurrType),
                        verificationData: new Set(verificationData)
                    });
                });
            }
            return {
                authenticationMethods: Array.from(mergedAuthMethods),
                assuranceData: {
                    verificationData: Array.from(mergedVerificationData)
                }
            };
        });
    }
    /**
     * Look through the memo and categorize the results based on what we can find with the requested
     * authentication method types.
     */
    askMemoForInfoOnRequestedTypes({ authenticationInstance, authenticationReason, requestedMethodTypes }) {
        return requestedMethodTypes.reduce((memoQueryResults, requestedMethodType) => {
            const { mergedAuthMethods, mergedVerificationData, pendingAuthMethodTypes } = memoQueryResults;
            const memoRecord = this.authenticationMethodMemo.get({
                accountReference: authenticationInstance.accountReference,
                authenticationReason,
                requestedMethodType
            });
            if (memoRecord) {
                // we got some methods for this query
                memoRecord.authenticationMethods.forEach((authenticationMethod) => {
                    mergedAuthMethods.add(authenticationMethod);
                });
                // we got some verification data for this query
                memoRecord.verificationData.forEach((verificationData) => {
                    mergedVerificationData.add(verificationData);
                });
            }
            else {
                // never attempted to find methods for this query
                pendingAuthMethodTypes.add(requestedMethodType);
            }
            return { mergedAuthMethods, mergedVerificationData, pendingAuthMethodTypes };
        }, {
            mergedAuthMethods: new Set(),
            mergedVerificationData: new Set(),
            pendingAuthMethodTypes: new Set()
        });
    }
    /**
     * Perform a lookup for any pending method types (i.e. haven't been attempted yet).
     *
     * Also generate our own auth methods based on what we already know that backend isn't currently
     * providing.
     */
    lookupForPendingMethodTypes({ authenticationInstance, authenticationReason, cardToken, idToken, pendingAuthMethodTypes }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (authenticationReason === AuthenticationReason.CARD_VERIFICATION) {
                return this.deriveCardVerificationAuthMethods();
            }
            if (authenticationInstance.accountReference.consumerIdentity) {
                return yield this.deriveConsumerSubjectAuthMethods({
                    authenticationInstance,
                    authenticationReason,
                    requestedMethodTypes: pendingAuthMethodTypes,
                    idToken
                });
            }
            return yield this.prepareAndPerformAuthProviderLookup({
                authenticationInstance,
                authenticationReason,
                cardToken,
                idToken,
                supportedMethodTypes: pendingAuthMethodTypes
            });
        });
    }
    /**
     * Auth provider doesn't support SMS and EMAIL OTP method types for CONSUMER subject, so filter
     * them out.
     */
    removeUnsupportedAuthProviderMethodTypesForConsumer({ authenticationMethodTypes }) {
        return authenticationMethodTypes
            .map((authMethodType) => authMethodType)
            .filter((authMethodType) => {
            const unsupportedAuthProviderMethodTypes = [
                AuthenticationMethodType.EMAIL_OTP,
                AuthenticationMethodType.SMS_OTP
            ];
            return !unsupportedAuthProviderMethodTypes.includes(authMethodType);
        });
    }
    /**
     * Create a consumer authentication method if we have enough information to do so. Then combine
     * this method with any other consumer method types that we need to fetch.
     *
     * This is a stop-gap until backend gives us authentication methods for consumer.
     */
    deriveConsumerSubjectAuthMethods({ authenticationInstance, authenticationReason, requestedMethodTypes, idToken }) {
        return __awaiter(this, void 0, void 0, function* () {
            // We have a consumer requested
            const { consumerIdentity } = authenticationInstance.accountReference;
            // We have a consumer defined
            const lookupToken = authenticationInstance.idLookupSessionId;
            // If we don't have both, then we don't have a consumer
            if (!(consumerIdentity === null || consumerIdentity === void 0 ? void 0 : consumerIdentity.identityType) || !lookupToken) {
                return { authenticationMethods: [] };
            }
            const consumerAuthenticationMethod = {
                authenticationMethodType: consumerIdentity.identityType === ConsumerIdentityType.MOBILE_PHONE_NUMBER
                    ? AuthenticationMethodType.SMS_OTP
                    : AuthenticationMethodType.EMAIL_OTP,
                authenticationCredentialReference: consumerIdentity.identityValue,
                authenticationSubject: AuthenticationSubject.CONSUMER
            };
            // Remove _OTP method types
            const pendingAuthProviderMethodTypes = this.removeUnsupportedAuthProviderMethodTypesForConsumer({
                authenticationMethodTypes: requestedMethodTypes
            });
            // If there are no additional pending consumer method types, just return consumer
            if (!pendingAuthProviderMethodTypes.length) {
                return { authenticationMethods: [consumerAuthenticationMethod] };
            }
            // Otherwise, if we need to fetch another consumer method type, do it
            const data = yield this.prepareAndPerformAuthProviderLookup({
                authenticationInstance,
                authenticationReason,
                supportedMethodTypes: pendingAuthProviderMethodTypes,
                idToken
            });
            return Object.assign(Object.assign({}, data), { authenticationMethods: data.authenticationMethods.concat(consumerAuthenticationMethod) });
        });
    }
    /**
     * Create a csc validation authentication method if we have enough information to do so.
     *
     * This is a stop-gap until backend gives us authentication methods for csc validation.
     */
    deriveCardVerificationAuthMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            return {
                authenticationMethods: [
                    {
                        authenticationMethodType: AuthenticationMethodType.CSC_VALIDATION,
                        authenticationSubject: AuthenticationSubject.CARD
                    }
                ]
            };
        });
    }
    /**
     * Build the authentication context in the format that the auth provider lookup call expects.
     */
    buildLookupAuthContext({ authenticationInstance, authenticationReason, supportedMethodTypes }) {
        return __awaiter(this, void 0, void 0, function* () {
            const contextOverrides = yield Promise.all(Object.values(authenticationInstance.adapters).map((strategy) => strategy.getLookupContext()));
            const mergedAuthContext = this.getAuthContextWithOverrides({
                authenticationInstance,
                contextOverrides,
                supportedMethodTypes
            });
            return Object.assign(Object.assign({}, mergedAuthContext), { authenticationReasons: [authenticationReason] });
        });
    }
    /**
     * Merge each context override on top of the initial context.
     */
    getAuthContextWithOverrides({ authenticationInstance, contextOverrides, supportedMethodTypes }) {
        const authenticationReasons = Object.keys(authenticationInstance.authenticationStrategies);
        const initialAuthenticationContext = mapAuthenticationContext({
            authenticationReasons,
            srcDpaId: this.starkInstanceParams.srcDpaId,
            dpaTransactionOptions: this.starkInstanceParams.dpaTransactionOptions,
            dpaData: this.starkInstanceParams.dpaData,
            supportedMethods: supportedMethodTypes
        });
        return contextOverrides.reduce((mergedAuthenticationContext, currContextOverride) => (Object.assign(Object.assign({}, mergedAuthenticationContext), currContextOverride)), initialAuthenticationContext);
    }
    /**
     * Prepare some additional needed context and then perform an auth provider lookup.
     */
    prepareAndPerformAuthProviderLookup({ authenticationInstance, authenticationReason, cardToken, idToken, supportedMethodTypes }) {
        return __awaiter(this, void 0, void 0, function* () {
            const authenticationContext = yield this.buildLookupAuthContext({
                authenticationInstance,
                authenticationReason,
                supportedMethodTypes
            });
            return yield this.performAuthProviderLookup({
                accountReference: authenticationInstance.accountReference,
                authenticatePayload: this.starkInstanceParams,
                authenticationContext,
                cardToken,
                idToken,
                idLookupSessionId: authenticationInstance.idLookupSessionId
            });
        });
    }
    /**
     * Pure auth provider lookup wrapper where we can do some error handling to ensure we're still
     * returning the correct response structure even if one of the many lookups fails.
     *
     * TODO: Improve error handling + log error in lower environments.
     */
    performAuthProviderLookup(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.kpi.resource = ((_a = params.accountReference) === null || _a === void 0 ? void 0 : _a.srcDigitalCardId)
                    ? KPI_RESOURCE.CARD
                    : KPI_RESOURCE.CONSUMER;
                this.kpi.start(KpiOperation.LOOKUP_AUTHENTICATION_METHODS, getLookupKpiMeta(params));
                const { data } = yield ApiService.authProviderLookup(params);
                this.kpi.end(KpiState.COMPLETED, getLookupKpiMeta(params, data));
                delete this.kpi.resource;
                return data;
            }
            catch (error) {
                this.kpi.queueError(error);
                this.kpi.end(KpiState.FAILED);
                if (axios.isAxiosError(error)) {
                    return { authenticationMethods: [] };
                }
                // Bubble up to alert for non-API errors.
                throw error;
            }
        });
    }
}
